import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { ApiDownloadButton } from "@/components/api-download-button";
import { apiFetch } from "@/services/http/api-fetch";

type Resource = { id: string; fileName: string; sizeBytes: number | null };

/**
 * H2: the course's Resources (PDFs for the whole course, not one session). Listed for enrolled
 * students and course staff; hidden when there are none or the list can't be read. Each Download
 * goes through the server's enrolment check (`?as=link`: a signed Bunny link, or the PDF itself).
 */
export function CourseResourcesPanel({ courseId }: { courseId: string }) {
  const t = useTranslations("player");
  const [resources, setResources] = useState<Resource[]>([]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void (async () => {
      try {
        const res = await apiFetch(`/v1/courses/${courseId}/resources`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const json = (await res.json()) as { data?: { resources?: Resource[] } };
        if (active) setResources(json.data?.resources ?? []);
      } catch {
        // An unreadable list just shows no Resources section.
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [courseId]);

  if (resources.length === 0) return null;

  return (
    <section data-testid="course-resources" aria-labelledby="course-resources-heading">
      <h2 id="course-resources-heading" className="text-lg font-medium">
        {t("courseResources")}
      </h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("courseResourcesHint")}</p>
      <ul className="mt-2 space-y-2">
        {resources.map((file) => (
          <li key={file.id}>
            <ApiDownloadButton
              path={`/v1/courses/${courseId}/resources/${file.id}/download?as=link`}
              fileName={file.fileName}
              className="text-left text-[var(--text-heading)] underline"
              data-testid={`course-resource-${file.id}`}
            >
              {file.fileName}
            </ApiDownloadButton>
          </li>
        ))}
      </ul>
    </section>
  );
}
